import React, { useCallback } from 'react';
import type { SecurityLevel } from '@/types/auth';
import { SECURITY_PRESETS } from '@/lib/score-utils';

interface SecuritySliderProps {
  /** Current security level */
  value: SecurityLevel;
  /** Called when level changes */
  onChange: (level: SecurityLevel) => void;
  /** Show detailed descriptions */
  showDetails?: boolean;
  /** Disabled state */
  disabled?: boolean;
}

const levels: SecurityLevel[] = ['convenient', 'balanced', 'strict'];

const levelConfig: Record<SecurityLevel, {
  label: string;
  description: string;
  icon: string;
  color: string;
}> = {
  convenient: {
    label: 'Conveniente',
    description: 'Limiar mais baixo para aceitacao. Ideal para aplicacoes de baixo risco. Menos verificacoes adicionais.',
    icon: '\🔓',
    color: '#4fc3f7',
  },
  balanced: {
    label: 'Equilibrado',
    description: 'Equilibrio entre seguranca e usabilidade. Recomendado para a maioria dos casos. Zona cinza exige step-up.',
    icon: '\u2696',
    color: '#7c5cfc',
  },
  strict: {
    label: 'Rigoroso',
    description: 'Limiar alto de aceitacao. Para aplicacoes criticas. Auth continua mais frequente.',
    icon: '\🛡',
    color: '#f5a623',
  },
};

/**
 * SecuritySlider — Three-step slider for security level selection.
 */
export default function SecuritySlider({
  value,
  onChange,
  showDetails = true,
  disabled = false,
}: SecuritySliderProps) {
  const currentIndex = levels.indexOf(value);
  const config = levelConfig[value];
  const preset = SECURITY_PRESETS[value];

  const handleClick = useCallback(
    (level: SecurityLevel) => {
      if (!disabled) onChange(level);
    },
    [disabled, onChange],
  );

  return (
    <div className={`w-full ${disabled ? 'opacity-50 pointer-events-none' : ''}`}>
      {/* Slider track */}
      <div className="relative mb-6">
        {/* Track background */}
        <div className="h-2 bg-bg-surface rounded-full relative">
          {/* Active fill */}
          <div
            className="absolute top-0 left-0 h-full rounded-full transition-all duration-300"
            style={{
              width: `${(currentIndex / (levels.length - 1)) * 100}%`,
              background: `linear-gradient(90deg, #4fc3f7, ${config.color})`,
            }}
          />
        </div>

        {/* Notches */}
        <div className="absolute top-1/2 left-0 right-0 -translate-y-1/2 flex justify-between">
          {levels.map((level, i) => {
            const isActive = i <= currentIndex;
            const lc = levelConfig[level];
            return (
              <button
                key={level}
                onClick={() => handleClick(level)}
                className={`
                  relative w-6 h-6 rounded-full border-2 transition-all duration-200
                  ${isActive
                    ? 'border-transparent scale-110'
                    : 'border-bg-surface bg-bg-primary hover:border-accent-brand/50'
                  }
                `}
                style={isActive ? { backgroundColor: lc.color } : undefined}
                title={lc.label}
              >
                {i === currentIndex && (
                  <span className="absolute inset-0 rounded-full animate-ping opacity-30"
                    style={{ backgroundColor: lc.color }}
                  />
                )}
              </button>
            );
          })}
        </div>
      </div>

      {/* Level labels */}
      <div className="flex justify-between mb-4">
        {levels.map((level) => {
          const lc = levelConfig[level];
          const isSelected = level === value;
          return (
            <button
              key={level}
              onClick={() => handleClick(level)}
              className={`
                text-xs font-sans font-medium transition-colors text-center
                ${isSelected ? '' : 'text-txt-muted hover:text-txt-primary'}
              `}
              style={isSelected ? { color: lc.color } : undefined}
            >
              <span className="block text-lg mb-0.5">{lc.icon}</span>
              {lc.label}
            </button>
          );
        })}
      </div>

      {/* Details */}
      {showDetails && (
        <div className="p-4 bg-bg-surface/30 rounded-card border border-bg-surface animate-fade-in">
          <div className="flex items-center gap-2 mb-2">
            <span className="text-xl">{config.icon}</span>
            <span className="text-sm font-medium font-sans" style={{ color: config.color }}>
              {config.label}
            </span>
          </div>
          <p className="text-xs text-txt-muted font-sans mb-3">
            {config.description}
          </p>
          <div className="grid grid-cols-2 gap-2 text-xs font-sans">
            <div className="flex justify-between p-2 bg-bg-primary/50 rounded-lg">
              <span className="text-txt-muted">Aceitar</span>
              <span className="text-txt-primary font-mono">&ge;{preset.acceptThreshold}%</span>
            </div>
            <div className="flex justify-between p-2 bg-bg-primary/50 rounded-lg">
              <span className="text-txt-muted">Rejeitar</span>
              <span className="text-txt-primary font-mono">&lt;{preset.rejectThreshold}%</span>
            </div>
            <div className="flex justify-between p-2 bg-bg-primary/50 rounded-lg">
              <span className="text-txt-muted">Auth cont.</span>
              <span className="text-txt-primary font-mono">{preset.continuousAuthInterval / 1000}s</span>
            </div>
            <div className="flex justify-between p-2 bg-bg-primary/50 rounded-lg">
              <span className="text-txt-muted">Step-up</span>
              <span className="text-txt-primary font-mono">{preset.stepUpRequired ? 'Sim' : 'Nao'}</span>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
